/*
  First-touch marketing attribution.

  The problem this solves: UTM and click-id parameters only exist in the URL of
  the page the visitor LANDS on. If they land on `/?utm_campaign=x`, browse to
  another page, and submit a form there, reading `window.location.search` at
  submit time returns nothing — the campaign that paid for the lead is lost.

  So we capture attribution once, on the first page view that carries any, and
  keep it in sessionStorage for the rest of the visit.

  First-touch (not last-touch) is deliberate: the campaign that brought someone
  to the site is the one that earned the lead, and internal navigation never
  carries UTMs anyway.
*/

const KEY = 'lead_attribution';

const FIELDS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid',
  'fbclid',
];

function readFromUrl() {
  const q = new URLSearchParams(window.location.search);
  const out = {};
  FIELDS.forEach((f) => {
    const v = q.get(f);
    if (v) out[f] = v;
  });
  return out;
}

/**
 * Call on every page view. Stores attribution the first time any is seen and
 * does not overwrite it afterwards, so internal navigation cannot blank it.
 */
export function captureAttribution() {
  if (typeof window === 'undefined') return;
  try {
    const fresh = readFromUrl();
    const hasFresh = Object.keys(fresh).length > 0;
    const stored = sessionStorage.getItem(KEY);

    if (!stored) {
      // Record the referrer even with no campaign params — it is how organic
      // and AI-assistant traffic gets identified later.
      sessionStorage.setItem(
        KEY,
        JSON.stringify({ ...fresh, referrer: document.referrer || '' })
      );
      return;
    }
    // Already stored. Only upgrade if this view carries campaign params and the
    // stored record has none (e.g. the visitor arrived organically, then came
    // back through an ad in the same session).
    if (hasFresh) {
      const prev = JSON.parse(stored) || {};
      const prevHasCampaign = FIELDS.some((f) => prev[f]);
      if (!prevHasCampaign) {
        sessionStorage.setItem(KEY, JSON.stringify({ ...prev, ...fresh }));
      }
    }
  } catch (_) {
    /* sessionStorage unavailable (private mode / blocked) — attribution is best-effort */
  }
}

/**
 * Attribution for the lead payload: stored first-touch values, with anything
 * currently in the URL taking precedence for this submission.
 */
export function getAttribution() {
  const base = {
    referrer: '',
    utm_source: '',
    utm_medium: '',
    utm_campaign: '',
    utm_term: '',
    utm_content: '',
    gclid: '',
    fbclid: '',
  };
  try {
    let stored = {};
    try {
      stored = JSON.parse(sessionStorage.getItem(KEY) || '{}') || {};
    } catch (_) {
      stored = {};
    }
    return {
      ...base,
      ...stored,
      ...readFromUrl(),
      referrer: stored.referrer || document.referrer || '',
    };
  } catch (_) {
    return base;
  }
}
